import { useRef, useCallback, useState, useEffect, Ref } from 'react';
import { useDrag, useScroll } from '@use-gesture/react';
import { useSpring, useSpringValue } from '@react-spring/web';

interface IScrollPage {
  /**
   * ref回调函数
   */
  ref: Ref<any>;
  /**
   * 当前页
   */
  current: number;
  /**
   * 总页数
   */
  total: number;
  /**
   * 拖动绑定
   */
  bind: any;

  style: any;

  onChange(page: number, isAnimated: boolean): void;
  // 滚动change
  onScrollChange(
    currentRange: number,
    ranges: [range1: number, range2: number]
  ): void;
}
const useScrollPage = ({
  total = 0,
  gap = 0,
  current = 0,
  scrollable = false,
  scrollCallback,
}: {
  total: number;
  gap: number;
  current?: number;
  // 是否可滚动模式
  scrollable?: boolean;
  scrollCallback?: (
    current: number,
    ranges: [range1: number, range2: number],
    active: boolean
  ) => void;
}): IScrollPage => {
  const [index, setIndex] = useState(current || 0);
  const offsetWidthRef = useRef(0);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const springValue = useSpringValue(0);
  const [style, api] = useSpring(() => ({
    x: 0,
  }));
  const ref = useCallback((node: HTMLDivElement | null) => {
    nodeRef.current = node;
    if (node) {
      const { offsetWidth } = node;
      if (offsetWidth > 0) {
        offsetWidthRef.current = offsetWidth;
      }
    }
  }, []);

  const bind = useDrag(
    ({ active, movement: [mx], cancel }) => {
      if (active) {
        api.start({ x: -index * offsetWidthRef.current + mx });
      } else {
        const _index = index + (mx > 0 ? -1 : 1);
        // 当用户松开屏幕时，判断是否需要切换图片
        if (_index >= 0 && _index < total && Math.abs(mx) > 50) {
          setIndex(_index);
          api.start({ x: -_index * offsetWidthRef.current + -_index * gap });
        } else {
          cancel();
          api.start({ x: -index * offsetWidthRef.current + -index * gap });
        }
      }
    },
    { filterTaps: true, axis: 'x' } // 过滤点击事件
  );
  //滑动
  const scrollableBind = useScroll(
    ({ offset: [ox], active }) => {
      // 计算滑块区间
      scrollCallback?.(
        ox,
        [0, (nodeRef.current?.scrollWidth || 0) - offsetWidthRef.current],
        active
      );
    },
    { filterTaps: true, axis: 'x' } // 过滤点击事件
  );

  // 滑动事件
  const handleScrollChange = (
    currentRange: number,
    ranges: [range1: number, range2: number]
  ) => {
    springValue.set(currentRange);
    const interpolations = springValue
      .to(ranges, [0, nodeRef.current?.scrollWidth || 0])
      .to((x) => x);
    nodeRef.current?.scrollTo(interpolations.get(), 0);
  };

  // 需要更新页的事件
  const handleChange = useCallback(
    (newIndex: number, isAnimated = true) => {
      const _index = index + (index - newIndex === 0 ? 0 : -(index - newIndex));
      // 拖拽需要
      if (isAnimated) {
        api.start({ x: -_index * offsetWidthRef.current + -_index * gap });
      }
      // 滑动需要
      if (scrollable) {
        const x = -_index * offsetWidthRef.current + -_index * gap;
        nodeRef.current?.scrollTo(-x, 0);
      }
      setIndex(newIndex);
    },
    [index]
  );

  useEffect(() => {
    handleChange(current);
  }, [current]);

  useEffect(() => {
    if (!nodeRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        ref(entries[0].target as HTMLDivElement);
      }
    });

    observer.observe(nodeRef.current);
    return () => observer.disconnect();
  }, []);

  return {
    ref,
    current: index,
    total,
    // 滑动拖拽二选一
    bind: scrollable ? scrollableBind : bind,
    style,
    onChange: handleChange,
    onScrollChange: handleScrollChange,
  };
};

export default useScrollPage;
